import {
  Box, Flex, Text,
} from '@chakra-ui/react';
import React, { useCallback, memo, useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import useGetCurrentUserId from '@app/selectors/user';

import QueueListRow from './queue-list-row';
import { useQueueAssign } from '../hooks';
import { QueueRow } from '../types';

interface QueueListProps {
  setIsServiceQueueOpen: (isOpen: boolean) => void;
  queuedJobs: QueueRow[];
  isLoading: boolean;
  loadMoreData: () => void;
  hasNextPage: boolean;
}

const QueueList: React.FC<QueueListProps> = ({
  setIsServiceQueueOpen,
  queuedJobs,
  isLoading,
  loadMoreData,
  hasNextPage,
}) => {
  const currentUserId = useGetCurrentUserId();
  const { assignUser, navigateToChat } = useQueueAssign({ setIsServiceQueueOpen });

  const handleAssignUser = useCallback(async (queueCase: QueueRow, newOperatorId: string) => {
    await assignUser(queueCase, newOperatorId);
    if (newOperatorId === currentUserId) {
      navigateToChat(queueCase._id);
    }
  }, [currentUserId, assignUser, navigateToChat]);

  const itemCount = hasNextPage ? queuedJobs.length + 1 : queuedJobs.length;
  const loadMoreItems = isLoading ? () => {} : loadMoreData;

  const MemoizedQueueListRow = memo(QueueListRow);

  // This function determines if an item at a given index is already loaded or should be loaded
  // It helps optimize performance by:
  // 1. Only loading items that are within the visible viewport plus a small buffer
  // 2. Preventing unnecessary data fetches for items far outside the visible range
  // 3. Ensuring smooth scrolling by pre-loading items just outside the viewport
  // The visibleRange is calculated based on the container height and item height (60px)
  // A buffer of 2 items is added to pre-load items just outside the viewport
  const isItemLoaded = useCallback((index: number, height: number) => {
    const visibleRange = Math.ceil(height / 60);
    const buffer = 2;
    return (
      !hasNextPage
      || index < queuedJobs.length
      || index > queuedJobs.length + visibleRange + buffer
    );
  }, [hasNextPage, queuedJobs.length]);

  const itemData = useMemo(() => ({
    queuedJobs,
    onAssignUser: handleAssignUser,
  }), [queuedJobs, handleAssignUser]);

  return (
    <Box height="100%" width="100%" overflow="hidden" data-testid="queue-list-container">
      <Flex direction="column" height="100%">
        {/* Header */}
        <Flex
          px={3}
          py={2.5}
          bg="gray.50"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          {/* Wait Time Column */}
          <Box width="100px" textAlign="center">
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">
              Wait Time
            </Text>
          </Box>

          {/* Customer Name Column */}
          <Box width="180px">
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">
              Customer Name
            </Text>
          </Box>

          {/* Message Column */}
          <Box flex="1">
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">
              Message
            </Text>
          </Box>

          {/* Assign Column */}
          <Box width="180px">
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">
              Assign
            </Text>
          </Box>
        </Flex>

        {/* Virtualized List */}
        <Box flex="1" overflow="hidden">
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                isItemLoaded={(index) => isItemLoaded(index, height)}
                itemCount={itemCount}
                loadMoreItems={loadMoreItems}
              >
                {({ onItemsRendered, ref }) => (
                  <FixedSizeList
                    ref={ref}
                    onItemsRendered={onItemsRendered}
                    height={height}
                    itemCount={queuedJobs.length}
                    itemSize={60}
                    width={width}
                    itemData={itemData}
                    style={{ overflowX: 'hidden' }}
                  >
                    {MemoizedQueueListRow}
                  </FixedSizeList>
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
        </Box>
      </Flex>
    </Box>
  );
};

export default QueueList;
